import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, MapPin, Layers, ZoomIn, ZoomOut, X } from 'lucide-react';
import { properties, formatNaira } from '../data/mockData';
import { PropertyCard } from '../components/shared/PropertyCard';
import { TierBadge } from '../components/shared/Badges';
export const MapDiscovery = () => {
  const [activePin, setActivePin] = useState<string | null>(properties[0].id);
  return <div className="h-screen pt-[72px] flex flex-col md:flex-row overflow-hidden bg-neutral-light" data-id="element-1768">
      {/* Left Sidebar: List */}
      <div className="w-full md:w-[400px] lg:w-[480px] h-1/2 md:h-full flex flex-col bg-white border-r border-neutral-200 shadow-xl z-10 shrink-0" data-id="element-1769">
        {/* Search & Filters */}
        <div className="p-4 border-b border-neutral-200 space-y-4" data-id="element-1770">
          <div className="relative" data-id="element-1771">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-dark" size={18} data-id="element-1772" />
            <input type="text" placeholder="Search Abuja districts..." className="w-full pl-10 pr-4 py-3 rounded-xl border border-neutral-200 bg-neutral-50 focus:bg-white focus:ring-2 focus:ring-primary-900 outline-none text-sm" data-id="element-1773" />
          </div>
          <div className="flex gap-2 overflow-x-auto custom-scrollbar pb-1" data-id="element-1774">
            <button className="px-4 py-1.5 rounded-full border border-neutral-200 text-sm font-medium whitespace-nowrap hover:border-primary-900 transition-colors flex items-center gap-1" data-id="element-1775">
              <Filter size={14} data-id="element-1776" /> Filters
            </button>
            <button className="px-4 py-1.5 rounded-full bg-secondary/10 text-secondary-dark border border-secondary/20 text-sm font-bold whitespace-nowrap" data-id="element-1777">
              Tier 1
            </button>
            <button className="px-4 py-1.5 rounded-full border border-neutral-200 text-sm font-medium whitespace-nowrap hover:border-primary-900 transition-colors" data-id="element-1778">
              Houses
            </button>
            <button className="px-4 py-1.5 rounded-full border border-neutral-200 text-sm font-medium whitespace-nowrap hover:border-primary-900 transition-colors" data-id="element-1779">
              Verified Only
            </button>
          </div>
        </div>

        {/* List */}
        <div className="flex-grow overflow-y-auto custom-scrollbar p-4 space-y-4 bg-neutral-50" data-id="element-1780">
          <div className="text-sm font-bold text-text-light mb-2" data-id="element-1781">
            Showing {properties.length} properties in map area
          </div>
          {properties.map(p => <div key={p.id} onMouseEnter={() => setActivePin(p.id)} className={`transition-transform duration-200 ${activePin === p.id ? '-translate-y-1' : ''}`} data-id="element-1782">
              <PropertyCard property={p} data-id="element-1783" />
            </div>)}
        </div>
      </div>

      {/* Right Area: Map Mockup */}
      <div className="flex-grow h-1/2 md:h-full relative bg-neutral-200" data-id="element-1784">
        <img src="https://images.unsplash.com/photo-1524661135-423995f22d0b?auto=format&fit=crop&q=80&w=2000" alt="Map Background" className="w-full h-full object-cover opacity-80" data-id="element-1785" />

        {/* Map Controls */}
        <div className="absolute top-4 right-4 flex flex-col gap-2" data-id="element-1786">
          <div className="bg-white rounded-xl shadow-lg border border-neutral-200 overflow-hidden flex flex-col" data-id="element-1787">
            <button className="p-3 text-text hover:bg-neutral-50 border-b border-neutral-100" data-id="element-1788">
              <ZoomIn size={20} data-id="element-1789" />
            </button>
            <button className="p-3 text-text hover:bg-neutral-50" data-id="element-1790">
              <ZoomOut size={20} data-id="element-1791" />
            </button>
          </div>
          <button className="p-3 bg-white rounded-xl shadow-lg border border-neutral-200 text-text hover:bg-neutral-50" data-id="element-1792">
            <Layers size={20} data-id="element-1793" />
          </button>
        </div>

        {/* Mock Pins */}
        {properties.map((p, i) => {
        // Randomize positions for mock
        const top = `${20 + i * 15}%`;
        const left = `${30 + i * 10}%`;
        const isActive = activePin === p.id;
        return <div key={p.id} className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer z-10" style={{
          top,
          left
        }} onClick={() => setActivePin(p.id)} data-id="element-1794">
              <div className={`relative group ${isActive ? 'z-20' : 'z-10'}`} data-id="element-1795">
                {/* Pin */}
                <div className={`px-3 py-1.5 rounded-full font-bold text-sm shadow-lg transition-all ${isActive ? 'bg-primary-900 text-white scale-110' : 'bg-white text-primary-900 border border-neutral-200 hover:scale-105'}`} data-id="element-1796">
                  {formatNaira(p.price)}
                </div>
                <div className={`w-3 h-3 rotate-45 absolute -bottom-1 left-1/2 -translate-x-1/2 ${isActive ? 'bg-primary-900' : 'bg-white border-r border-b border-neutral-200'}`} data-id="element-1797"></div>

                {/* Popup Card */}
                {isActive && <motion.div initial={{
              opacity: 0,
              y: 10,
              scale: 0.95
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} className="absolute bottom-full left-1/2 -translate-x-1/2 mb-4 w-64 bg-white rounded-2xl shadow-xl border border-neutral-200 overflow-hidden pointer-events-none" data-id="element-1798">
                    <img src={p.images[0]} className="w-full h-32 object-cover" data-id="element-1799" />
                    <div className="p-3" data-id="element-1800">
                      <div className="flex items-center gap-2 mb-1" data-id="element-1801">
                        <TierBadge tier={p.tier as 1 | 2 | 3} data-id="element-1802" />
                      </div>
                      <h4 className="font-bold text-sm text-text line-clamp-1" data-id="element-1803">
                        {p.title}
                      </h4>
                      <p className="text-xs text-text-light mt-1 flex items-center gap-1" data-id="element-1804">
                        <MapPin size={12} data-id="element-1805" /> {p.district}
                      </p>
                    </div>
                  </motion.div>}
              </div>
            </div>;
      })}
      </div>
    </div>;
};